/**
 * UPLOAD AUDIO — bring your own file in, for real.
 *
 * Choosing or dropping a file, rejecting the wrong format or an oversized one with a
 * message that names the limit, reading the file's first bytes and reporting the
 * container it actually is, and a title defaulted from the filename and editable — all
 * of that was already real and unchanged. What used to end there now continues: the
 * accepted file is sent to `POST /uploads/audio`, which independently re-decodes it with
 * ffmpeg (a client's sniffed container is a claim, not a proof — see
 * server/src/routes/audioUpload.ts), stores it and returns a new `Song`, which is added
 * to the library exactly like a generated one.
 *
 * THE ONE REMAINING DISABLED STATE is `!support.available` — no backend configured
 * (`VITE_API_BASE_URL` unset). That is genuinely still true in some environments, so the
 * button stays disabled with the reason attached rather than pretending otherwise, the
 * same convention TrimPanel's `unavailableButton` uses.
 *
 * See src/api/audioUploadApi.ts and docs/api-contract.md §24.
 */
import { useState } from 'react';
import type { CSSProperties } from 'react';
import { FileAudio, Loader2, Upload, X } from 'lucide-react';
import { Btn, IconButton, Tooltip } from '../ui';
import DrawerHeader from './DrawerHeader';
import { audioUploadApi } from '../../api/audioUploadApi';
import {
  ACCEPTED_AUDIO_LABEL,
  AUDIO_ACCEPT_ATTRIBUTE,
  AUDIO_CONTAINER_LABEL,
  MAX_AUDIO_LABEL,
  firstAudioFrom,
  inspectAudioFile,
  validateAudioFile,
} from '../../lib/audioUpload';
import { formatBytes } from '../../lib/imageUpload';
import { c } from '../../styles/theme';
import { useStudioStore } from '../../stores/studioStore';
import type { AudioContainer } from '../../lib/audioUpload';
import type { Song } from '../../types/domain';

export interface UploadPanelProps {
  onBack: () => void;
  onClose: () => void;
  /**
   * Sends the accepted file to the backend and adds the result to the library.
   *
   * Never rejects: `useSongs().uploadAudioFile` catches its own failures, toasts them,
   * and resolves `null` — the same "no song was made" contract `createSong` uses — so
   * this panel only has to react to the return value, not to a caught error.
   */
  onUpload: (input: {
    file: File;
    container: AudioContainer;
    title: string;
    artistName: string;
    genre: string;
    artistGender: string;
    uploaderName: string;
  }) => Promise<Song | null>;
}

const LABEL: CSSProperties = { fontSize: 12, color: c.text2, margin: '0 0 8px' };

/** A file that passed validation and inspection, with the title it would be saved as. */
interface Accepted {
  file: File;
  container: AudioContainer;
}

/** The filename without its extension, as the default title. */
function titleFromFilename(name: string): string {
  const dot = name.lastIndexOf('.');
  const stem = dot < 1 ? name : name.slice(0, dot);
  // Underscores and hyphens are how filenames spell spaces. Trimmed so a name that is
  // only punctuation does not become a title of whitespace.
  return stem.replace(/[_-]+/g, ' ').trim() || name;
}

export default function UploadPanel({ onBack, onClose, onUpload }: UploadPanelProps) {
  /** True while a file is dragged over the box. Presentation only. */
  const [over, setOver] = useState(false);
  /** True while the file's first bytes are being read. A real wait, not a simulated one. */
  const [inspecting, setInspecting] = useState(false);
  /** Feedback about the last pick or submit. Not carried in studioStore: it describes
      the last ACTION taken in this panel, not what was chosen, and a resumed panel
      showing a stale rejection for a file that was already accepted would be confusing. */
  const [error, setError] = useState<string | null>(null);

  /*
    Read LIVE from studioStore rather than a one-time `useState` initializer — see the
    field comments on StudioState.uploadFile and .uploadSubmitting for why. In short:
    the upload request is a plain async call with no lifecycle tie to this component, so
    if the user leaves Upload mid-request and comes back, this mount must see the SAME
    file, title and in-flight state the previous one did, including a submit finishing
    while nobody is looking at it.
  */
  const uploadFile = useStudioStore((s) => s.uploadFile);
  const uploadContainer = useStudioStore((s) => s.uploadContainer);
  const title = useStudioStore((s) => s.uploadTitle);
  const artistName = useStudioStore((s) => s.uploadArtistName);
  const genre = useStudioStore((s) => s.uploadGenre);
  const artistGender = useStudioStore((s) => s.uploadArtistGender);
  const uploaderName = useStudioStore((s) => s.uploadUploaderName);
  const submitting = useStudioStore((s) => s.uploadSubmitting);
  const accepted: Accepted | null =
    uploadFile && uploadContainer ? { file: uploadFile, container: uploadContainer } : null;

  /*
    Read through the API rather than hardcoded, so a change in configuration (or in
    tests) is reflected without an edit here. See audioUploadApi.supports().
  */
  const support = audioUploadApi.support();

  async function take(list: FileList | null) {
    const file = firstAudioFrom(list);
    if (!file) {
      setError('No file was found in that drop. Pick one audio file.');
      return;
    }

    // Metadata first, synchronously: format and size are decided without reading the
    // file, so an obviously wrong pick is refused instantly rather than after a read.
    const rejection = validateAudioFile(file);
    if (rejection) {
      setError(rejection.message);
      // The previous accepted file is left in place. A bad second pick should not
      // discard a good first one — the same rule the cover uploader follows.
      return;
    }

    setError(null);
    setInspecting(true);
    try {
      const container = await inspectAudioFile(file);
      useStudioStore.getState().setUploadForm({
        uploadFile: file,
        uploadContainer: container,
        uploadTitle: titleFromFilename(file.name),
        uploadArtistName: '',
        uploadGenre: '',
        uploadArtistGender: '',
        uploadUploaderName: '',
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not read that audio file.');
    } finally {
      // In `finally` so a failed read cannot leave the box spinning.
      setInspecting(false);
    }
  }

  function clear() {
    useStudioStore.getState().setUploadForm({
      uploadFile: null,
      uploadContainer: null,
      uploadTitle: '',
      uploadArtistName: '',
      uploadGenre: '',
      uploadArtistGender: '',
      uploadUploaderName: '',
    });
    setError(null);
  }

  /**
   * Sends the accepted file. Leaves the panel on success; stays put and shows the reason
   * on failure — `onUpload` never rejects, it resolves `null` and has already toasted why
   * (see useSongs.uploadAudioFile).
   *
   * `uploadSubmitting` and the clearing of the accepted file below are both written to
   * studioStore rather than local state, and deliberately NOT into a variable captured by
   * this closure: this async function keeps running even if the panel unmounts (leaving
   * Upload for another tool), so its own updates on the way out must land somewhere a
   * LATER mount of this panel will actually see, not into a component instance that may
   * no longer exist by the time this resolves.
   */
  async function submit() {
    if (!accepted) return;
    useStudioStore.getState().setUploadSubmitting(true);
    setError(null);
    try {
      const song = await onUpload({
        file: accepted.file,
        container: accepted.container,
        title: title.trim() || titleFromFilename(accepted.file.name),
        artistName: artistName.trim(),
        genre: genre.trim(),
        artistGender: artistGender.trim(),
        uploaderName: uploaderName.trim(),
      });
      if (song) {
        useStudioStore.getState().setUploadForm({
          uploadFile: null,
          uploadContainer: null,
          uploadTitle: '',
          uploadArtistName: '',
          uploadGenre: '',
          uploadArtistGender: '',
          uploadUploaderName: '',
        });
        // Only follow the user back to the overview if Upload is still the open drawer —
        // the request can finish long after they moved to a different tool (or closed
        // Studio outright), and yanking them away from whatever they are looking at now
        // would be a surprise nobody asked for. The song is already in the library and
        // already toasted either way (useSongs.uploadAudioFile).
        if (useStudioStore.getState().drawer === 'upload') onBack();
      }
    } finally {
      useStudioStore.getState().setUploadSubmitting(false);
    }
  }

  const busy = inspecting || submitting;

  return (
    <div className="lyx-tool-panel">
      <DrawerHeader title="Upload Audio" onBack={onBack} onClose={onClose} />

      <p style={{ fontSize: 12, color: c.text2, margin: '0 0 14px' }}>
        Bring your own recording in. {ACCEPTED_AUDIO_LABEL}, up to {MAX_AUDIO_LABEL}.
      </p>

      {/*
        The drop target. A <label>, so the visually hidden input inside is activated by
        click, tap, Enter and Space with no keydown handler of our own — the same pairing
        CoverPreview uses, and for the same reason. Its consequence applies here too: a
        button inside a label also activates the label, so Remove sits in the row BELOW
        the box rather than within it.
      */}
      <label
        className="lyx-audio-drop"
        onDragOver={(e) => {
          // Required. Without it the browser handles the drop itself and navigates away
          // from the app to play the file.
          e.preventDefault();
          if (!busy) setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setOver(false);
          if (!busy) void take(e.dataTransfer?.files ?? null);
        }}
        style={{
          background: over ? c.accentSoft : c.surface2,
          border: `1px dashed ${over ? c.accentBorder : c.borderStrong}`,
          cursor: busy ? 'default' : 'pointer',
        }}
      >
        <input
          type="file"
          accept={AUDIO_ACCEPT_ATTRIBUTE}
          disabled={busy}
          className="lyx-visually-hidden"
          // An explicit name: the label's text content changes with the state, and this
          // says which action it is — over a chosen file the picker replaces it.
          aria-label={accepted ? 'Replace the chosen audio file' : 'Choose an audio file'}
          onChange={(e) => {
            void take(e.target.files);
            // Cleared so picking the same file twice fires onChange the second time.
            // Without this, re-choosing after a Remove silently does nothing.
            e.target.value = '';
          }}
        />

        {inspecting ? (
          <div style={{ textAlign: 'center' }}>
            <Loader2 size={18} color={c.accent} className="lyx-spin" />
            <p style={{ fontSize: 12, color: c.text2, margin: '10px 0 0' }}>Reading the file…</p>
          </div>
        ) : accepted ? (
          <div style={{ textAlign: 'center', minWidth: 0, maxWidth: '100%' }}>
            <FileAudio size={20} color={c.accent} aria-hidden="true" />
            {/* The filename can be anything, so it wraps rather than widening the panel —
                a long name must not push the box sideways at 380px. */}
            <p
              style={{
                fontSize: 12.5,
                color: c.text1,
                margin: '8px 0 2px',
                overflowWrap: 'anywhere',
              }}
            >
              {accepted.file.name}
            </p>
            <p style={{ fontSize: 11.5, color: c.text2, margin: 0, fontFamily: 'IBM Plex Mono' }}>
              {AUDIO_CONTAINER_LABEL[accepted.container]} · {formatBytes(accepted.file.size)}
            </p>
          </div>
        ) : (
          <div style={{ textAlign: 'center' }}>
            <Upload size={20} color={c.text2} aria-hidden="true" />
            <p style={{ fontSize: 12.5, color: c.text1, margin: '8px 0 2px' }}>
              Click, or drop a file here
            </p>
            <p style={{ fontSize: 11.5, color: c.text2, margin: 0 }}>{ACCEPTED_AUDIO_LABEL}</p>
          </div>
        )}
      </label>

      {/*
        One live region for the whole panel, always present so it is not a node appearing
        and disappearing from the accessibility tree. It carries a rejection when there is
        one and what was detected when there is not — the same shape as TrimPanel's.
        `polite` because it changes as files are picked and `assertive` would interrupt.
      */}
      <p
        id="studio-upload-status"
        role="status"
        aria-live="polite"
        style={{
          fontSize: 11.5,
          margin: '10px 0 0',
          minHeight: 16,
          color: error ? c.danger : c.text2,
          fontFamily: error ? 'Inter' : 'IBM Plex Mono',
        }}
      >
        {error ??
          (accepted
            ? `Detected ${AUDIO_CONTAINER_LABEL[accepted.container]} from the file's own bytes`
            : '')}
      </p>

      {accepted && (
        <>
          {/* Outside the label, because a button inside it would reopen the picker. */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
            <Tooltip label="Remove this file and choose another">
              <IconButton label="Remove the chosen audio file" nativeTitle={false} onClick={clear}>
                <X size={13} color={c.text2} />
              </IconButton>
            </Tooltip>
          </div>

          <label
            htmlFor="studio-upload-title"
            style={{ ...LABEL, display: 'block', marginTop: 6 }}
          >
            Title
          </label>
          {/* `width: 100%`, which it did not have: .lyx-tool-field carries only the font
              size, so this was a bare input at its intrinsic ~20-character width — narrower
              than the panel on a desktop and, once the coarse-pointer rule takes the type to
              16px, wider than the panel at 380px. The other fields in these panels all state
              it; this one was the omission. */}
          <input
            id="studio-upload-title"
            className="lyx-tool-field"
            value={title}
            onChange={(e) =>
              useStudioStore.getState().setUploadForm({
                uploadFile: accepted.file,
                uploadContainer: accepted.container,
                uploadTitle: e.target.value,
                uploadArtistName: artistName,
                uploadGenre: genre,
                uploadArtistGender: artistGender,
                uploadUploaderName: uploaderName,
              })
            }
            placeholder={titleFromFilename(accepted.file.name)}
            style={{
              width: '100%',
              background: c.surface2,
              border: `1px solid ${c.border}`,
              borderRadius: 8,
              padding: '8px 10px',
              color: c.text1,
            }}
          />

          <label htmlFor="studio-upload-artist-name" style={{ ...LABEL, display: 'block', marginTop: 12 }}>
            Name of Artist
          </label>
          <input
            id="studio-upload-artist-name"
            className="lyx-tool-field"
            value={artistName}
            onChange={(e) =>
              useStudioStore.getState().setUploadForm({
                uploadFile: accepted.file,
                uploadContainer: accepted.container,
                uploadTitle: title,
                uploadArtistName: e.target.value,
                uploadGenre: genre,
                uploadArtistGender: artistGender,
                uploadUploaderName: uploaderName,
              })
            }
            style={{
              width: '100%',
              background: c.surface2,
              border: `1px solid ${c.border}`,
              borderRadius: 8,
              padding: '8px 10px',
              color: c.text1,
            }}
          />

          <label htmlFor="studio-upload-genre" style={{ ...LABEL, display: 'block', marginTop: 12 }}>
            Genre
          </label>
          <input
            id="studio-upload-genre"
            className="lyx-tool-field"
            value={genre}
            onChange={(e) =>
              useStudioStore.getState().setUploadForm({
                uploadFile: accepted.file,
                uploadContainer: accepted.container,
                uploadTitle: title,
                uploadArtistName: artistName,
                uploadGenre: e.target.value,
                uploadArtistGender: artistGender,
                uploadUploaderName: uploaderName,
              })
            }
            style={{
              width: '100%',
              background: c.surface2,
              border: `1px solid ${c.border}`,
              borderRadius: 8,
              padding: '8px 10px',
              color: c.text1,
            }}
          />

          <label htmlFor="studio-upload-artist-gender" style={{ ...LABEL, display: 'block', marginTop: 12 }}>
            Gender
          </label>
          <input
            id="studio-upload-artist-gender"
            className="lyx-tool-field"
            value={artistGender}
            onChange={(e) =>
              useStudioStore.getState().setUploadForm({
                uploadFile: accepted.file,
                uploadContainer: accepted.container,
                uploadTitle: title,
                uploadArtistName: artistName,
                uploadGenre: genre,
                uploadArtistGender: e.target.value,
                uploadUploaderName: uploaderName,
              })
            }
            style={{
              width: '100%',
              background: c.surface2,
              border: `1px solid ${c.border}`,
              borderRadius: 8,
              padding: '8px 10px',
              color: c.text1,
            }}
          />

          <label htmlFor="studio-upload-uploader-name" style={{ ...LABEL, display: 'block', marginTop: 12 }}>
            Uploader Name
          </label>
          <input
            id="studio-upload-uploader-name"
            className="lyx-tool-field"
            value={uploaderName}
            onChange={(e) =>
              useStudioStore.getState().setUploadForm({
                uploadFile: accepted.file,
                uploadContainer: accepted.container,
                uploadTitle: title,
                uploadArtistName: artistName,
                uploadGenre: genre,
                uploadArtistGender: artistGender,
                uploadUploaderName: e.target.value,
              })
            }
            style={{
              width: '100%',
              background: c.surface2,
              border: `1px solid ${c.border}`,
              borderRadius: 8,
              padding: '8px 10px',
              color: c.text1,
            }}
          />
        </>
      )}

      {/*
        The submit control. Real once a backend is configured: a click sends the file and,
        on success, returns to the tool rail \u2014 the new song is a library entry, not a
        version of whatever was open here. Wrapped in a span when disabled because a
        disabled button fires no pointer events, and without the wrapper the tooltip would
        never open on the one control that most needs to explain itself. TrimPanel's exact
        arrangement.
      */}
      {support.available ? (
        <Btn
          full
          disabled={
            accepted === null ||
            busy ||
            !title.trim() ||
            !artistName.trim() ||
            !genre.trim() ||
            !artistGender.trim() ||
            !uploaderName.trim()
          }
          aria-label={submitting ? 'Adding to library\u2026' : 'Add to library'}
          aria-describedby="studio-upload-status"
          onClick={() => void submit()}
          style={{ marginTop: 14 }}
        >
          {submitting ? (
            <Loader2 size={13} className="lyx-spin" />
          ) : (
            <Upload size={13} />
          )}
          {submitting ? 'Adding\u2026' : 'Add to library'}
        </Btn>
      ) : (
        <Tooltip label={support.blockers[0]?.summary ?? 'Not available yet.'}>
          <span style={{ display: 'block', width: '100%', marginTop: 14 }}>
            <Btn
              full
              disabled
              aria-label="Add to library — not available yet"
              aria-describedby="studio-upload-status"
            >
              <Upload size={13} />
              Add to library
            </Btn>
          </span>
        </Tooltip>
      )}
    </div>
  );
}
